/**
 * Board Configuration for Snakes and Ladders
 * Contains default board configs and validation functions
 */

/**
 * Classic UK/India Snakes and Ladders board configuration (10x10)
 * Based on traditional board layout
 */
export const CLASSIC_CONFIG_10x10 = {
    rows: 10,
    cols: 10,
    snakes: [
        { start: 17, end: 7 },
        { start: 54, end: 34 },
        { start: 62, end: 18 },
        { start: 64, end: 60 },
        { start: 87, end: 24 },
        { start: 93, end: 73 },
        { start: 95, end: 75 },
        { start: 98, end: 79 },
    ],
    ladders: [
        { start: 4, end: 14 },
        { start: 9, end: 31 },
        { start: 21, end: 42 },
        { start: 28, end: 84 },
        { start: 51, end: 67 },
        { start: 72, end: 91 },
        { start: 80, end: 99 },
    ],
};

/**
 * Medium board configuration (8x8)
 * Scaled down version with proportional snake/ladder placement
 */
export const CLASSIC_CONFIG_8x8 = {
    rows: 8,
    cols: 8,
    snakes: [
        { start: 14, end: 6 },
        { start: 35, end: 22 },
        { start: 47, end: 15 },
        { start: 56, end: 19 },
        { start: 62, end: 48 },
    ],
    ladders: [
        { start: 3, end: 11 },
        { start: 8, end: 25 },
        { start: 17, end: 34 },
        { start: 41, end: 54 },
        { start: 52, end: 63 },
    ],
};

/**
 * Small board configuration (5x5)
 * Quick game with fewer snakes and ladders
 */
export const CLASSIC_CONFIG_5x5 = {
    rows: 5,
    cols: 5,
    snakes: [
        { start: 17, end: 7 },
        { start: 21, end: 9 },
        { start: 24, end: 16 },
    ],
    ladders: [
        { start: 3, end: 11 },
        { start: 6, end: 14 },
        { start: 10, end: 22 },
    ],
};

/**
 * Get default config for a board size
 * @param {number} rows - Number of rows
 * @returns {object} Board configuration
 */
export const getDefaultConfig = (rows) => {
    switch (rows) {
        case 5:
            return CLASSIC_CONFIG_5x5;
        case 8:
            return CLASSIC_CONFIG_8x8;
        case 10:
        default:
            return CLASSIC_CONFIG_10x10;
    }
};

/**
 * Validation result type
 * @typedef {object} ValidationResult
 * @property {boolean} valid - Whether the config is valid
 * @property {string[]} errors - Array of error messages
 */

/**
 * Validate a board configuration
 * @param {object} config - Board configuration
 * @returns {ValidationResult} Validation result
 */
export const validateBoardConfig = (config) => {
    const errors = [];
    const { rows, cols, snakes, ladders } = config;
    const maxSquare = rows * cols;
    const usedSquares = new Set();

    // Validate snakes
    snakes.forEach((snake, index) => {
        // Snake must go backwards
        if (snake.start <= snake.end) {
            errors.push(`Snake ${index + 1}: start (${snake.start}) must be greater than end (${snake.end})`);
        }

        // Check bounds
        if (snake.start < 1 || snake.start > maxSquare) {
            errors.push(`Snake ${index + 1}: start (${snake.start}) out of bounds (1-${maxSquare})`);
        }
        if (snake.end < 1 || snake.end > maxSquare) {
            errors.push(`Snake ${index + 1}: end (${snake.end}) out of bounds (1-${maxSquare})`);
        }

        // Check for duplicates
        if (usedSquares.has(snake.start)) {
            errors.push(`Snake ${index + 1}: start (${snake.start}) already used by another snake/ladder`);
        }
        if (usedSquares.has(snake.end)) {
            errors.push(`Snake ${index + 1}: end (${snake.end}) already used by another snake/ladder`);
        }
        usedSquares.add(snake.start);
        usedSquares.add(snake.end);
    });

    // Validate ladders
    ladders.forEach((ladder, index) => {
        // Ladder must go forwards
        if (ladder.start >= ladder.end) {
            errors.push(`Ladder ${index + 1}: start (${ladder.start}) must be less than end (${ladder.end})`);
        }

        // Ladder cannot start on square 1
        if (ladder.start === 1) {
            errors.push(`Ladder ${index + 1}: cannot start on square 1`);
        }

        // Check bounds
        if (ladder.start < 1 || ladder.start > maxSquare) {
            errors.push(`Ladder ${index + 1}: start (${ladder.start}) out of bounds (1-${maxSquare})`);
        }
        if (ladder.end < 1 || ladder.end > maxSquare) {
            errors.push(`Ladder ${index + 1}: end (${ladder.end}) out of bounds (1-${maxSquare})`);
        }

        // Check for duplicates
        if (usedSquares.has(ladder.start)) {
            errors.push(`Ladder ${index + 1}: start (${ladder.start}) already used by another snake/ladder`);
        }
        if (usedSquares.has(ladder.end)) {
            errors.push(`Ladder ${index + 1}: end (${ladder.end}) already used by another snake/ladder`);
        }
        usedSquares.add(ladder.start);
        usedSquares.add(ladder.end);
    });

    return {
        valid: errors.length === 0,
        errors
    };
};

/**
 * Get square type for a given square number
 * @param {number} squareNumber - Square to check
 * @param {Array} snakes - Array of snakes
 * @param {Array} ladders - Array of ladders
 * @returns {'blank'|'snake_start'|'snake_end'|'ladder_start'|'ladder_end'} Square type
 */
export const getSquareType = (squareNumber, snakes, ladders) => {
    for (const snake of snakes) {
        if (snake.start === squareNumber) return 'snake_start';
        if (snake.end === squareNumber) return 'snake_end';
    }
    for (const ladder of ladders) {
        if (ladder.start === squareNumber) return 'ladder_start';
        if (ladder.end === squareNumber) return 'ladder_end';
    }
    return 'blank';
};

/**
 * Compute board state from configuration
 * @param {object} config - Board configuration
 * @returns {Map<number, object>} Map of square number to state
 */
export const computeBoardState = (config) => {
    const { rows, cols, snakes, ladders } = config;
    const state = new Map();

    for (let i = 1; i <= rows * cols; i++) {
        state.set(i, {
            squareNumber: i,
            type: getSquareType(i, snakes, ladders)
        });
    }

    // Add destination info for snake/ladder starts
    snakes.forEach(snake => {
        const squareState = state.get(snake.start);
        squareState.destination = snake.end;
    });

    ladders.forEach(ladder => {
        const squareState = state.get(ladder.start);
        squareState.destination = ladder.end;
    });

    return state;
};

/**
 * Format config for display
 * @param {object} config - Board configuration
 * @returns {object} Formatted display strings
 */
export const formatConfigForDisplay = (config) => {
    const { snakes, ladders } = config;

    return {
        snakes: snakes.map(s => `${s.start}→${s.end}`).join(', '),
        ladders: ladders.map(l => `${l.start}→${l.end}`).join(', ')
    };
};
